/**
 * De donde sale cada barra del Gantt, en palabras.
 *
 * Esta pantalla tiene un problema que no se ve: **casi ninguna fecha la puso una persona**.
 * `services/task_schedule.py` las deduce —del alta del ticket, de las horas estimadas, o de un
 * default de 8 h cuando no hay estimacion— y despues las dibuja igual que a las confirmadas.
 * Quien abre el Gantt por primera vez lee fechas y no tiene manera de saber cual es un plan y
 * cual una suposicion.
 *
 * El tooltip nativo mostraba `label / start → end / progreso` y ningun porque. Aca esta el
 * porque, en un solo lugar, para que la tarjeta de la barra y la guia lateral no puedan
 * contradecirse.
 */

// El mismo default que usa el backend (services/task_schedule.py::DEFAULT_HOURS). Si alla
// cambia, aca miente: por eso va nombrado y no suelto en un string.
export const DEFAULT_HORAS = 8;

// **La jornada la declara la capsula, y este 8 es solo el respaldo.** Esta constante decia 8 y se
// usaba como si fuera la verdad: el backend calcula la duracion con `horas_por_dia(horario)` de la
// capsula y **manda el dato** en `meta.horas_por_dia` justamente para que cliente y servidor
// coincidan —sin eso, el largo que se dibuja al arrastrar no coincide con el que devuelve el diff—.
// `explicarFila` no lo usaba, asi que en una capsula de jornada de 6 h el tooltip decia
// "8 h estimadas ÷ 8 h por dia = 1 dia" mientras el backend habia calculado 2. El comentario de
// arriba ya lo admitia: *"si alla cambia, aca miente"*. Ya cambio.
export const HORAS_POR_DIA_POR_OMISION = 8;

const TIPO = {
  ticket: {
    label: 'Ticket',
    nota: 'La barra es el rollup de sus tareas: arranca con la primera y termina con la ultima. No es una fecha propia ni lo prometido al usuario.',
  },
  phase: {
    label: 'Fase',
    nota: 'Rollup de las tareas de la fase. Tampoco es una fecha propia.',
  },
  task: {
    label: 'Tarea',
    nota: 'La unica fila con fechas propias.',
  },
};

function dia(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y?.slice(2)}`;
}

export function diasDeBarra(inicio, fin) {
  if (!inicio || !fin) return null;
  const a = new Date(`${String(inicio).slice(0, 10)}T12:00:00`);
  const b = new Date(`${String(fin).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.round((b - a) / 86400000) + 1;
}

/**
 * Cuantos dias sale una barra de N horas, **con la jornada de la capsula**.
 *
 * Espeja `task_schedule.dias_de_trabajo`, que divide por `horas_por_dia(horario)`. La jornada llega
 * en `meta.horas_por_dia` y el 8 es solo el respaldo para cuando todavia no cargo.
 */
export function diasPorHoras(horas, horasPorDia) {
  const jornada = Number(horasPorDia) > 0 ? Number(horasPorDia) : HORAS_POR_DIA_POR_OMISION;
  return Math.max(1, Math.ceil((Number(horas) || DEFAULT_HORAS) / jornada));
}

/**
 * Traduce una fila del Gantt a algo que se pueda leer sin conocer el modelo.
 * Devuelve `{ tipoLabel, tipoNota, campos, avisos, progreso }`.
 */
export function explicarFila(row, { horasPorDia } = {}) {
  if (!row) return null;
  const jornada = Number(horasPorDia) > 0 ? Number(horasPorDia) : HORAS_POR_DIA_POR_OMISION;
  const tipo = TIPO[row.row_type] || TIPO.task;
  const esTarea = row.row_type === 'task';
  const campos = [];
  const avisos = [];

  const inferido = row.start_inferred || row.end_inferred;
  const est = Number(row.estimate_hours) || 0;

  campos.push({
    k: 'Desde',
    v: dia(row.start),
    nota: row.start_inferred ? 'lo dedujo el sistema' : 'confirmado',
    flojo: !!row.start_inferred,
  });
  campos.push({
    k: 'Hasta',
    v: dia(row.end),
    nota: row.end_inferred ? 'lo dedujo el sistema' : 'confirmado',
    flojo: !!row.end_inferred,
  });

  const largo = diasDeBarra(row.start, row.end);
  if (largo) campos.push({ k: 'Dura', v: largo === 1 ? '1 dia' : `${largo} dias`, nota: '', flojo: false });

  if (esTarea) {
    campos.push({
      k: 'Estimado',
      v: est ? `${est} h` : 'sin estimar',
      nota: est ? '' : `la barra usa el default de ${DEFAULT_HORAS} h`,
      flojo: !est,
    });
    if (row.consumed_hours) {
      const imp = row.imputaciones || {};
      const n = Number(imp.n) || 0;
      campos.push({
        k: 'Consumido',
        v: `${row.consumed_hours} h`,
        nota: n ? `${n} imputacion${n === 1 ? '' : 'es'}` : '',
        flojo: false,
      });
      if (imp.sin_nota) {
        avisos.push(
          `${imp.sin_nota} de ${n} imputacion(es) no dicen en que se fueron esas horas. ` +
          'Se cargaron antes de que la nota fuera obligatoria.',
        );
      }
    }
  }

  // Los avisos son lo que cambia una decision, no adorno. Van en orden de que tan enganoso es
  // lo que se esta mirando.
  if (esTarea && inferido && !est) {
    avisos.push(
      `Nadie estimo esta tarea. El largo de la barra sale de un default de ${DEFAULT_HORAS} h, no de cuanto trabajo hay.`,
    );
  } else if (esTarea && inferido && est) {
    avisos.push(
      `Fecha deducida: ${est} h estimadas ÷ ${jornada} h por dia = ${diasPorHoras(est, jornada)} ${diasPorHoras(est, jornada) === 1 ? 'dia' : 'dias'}.`,
    );
  } else if (!esTarea && inferido) {
    // **El aviso decia algo que no habia pasado.** Cuando un ticket no tiene tareas **ni**
    // `planned_start`, el backend le inventa siete dias desde `created_at`
    // (`gantt_schedule.py`): no hay tareas que sumar, y la barra no significa nada. El texto
    // afirmaba "esto es lo que suman sus tareas" justamente en el caso donde no suman nada.
    //
    // `rollup_de` lo dice el servidor —cuantas tareas entraron al rollup— y con 0 el aviso cambia.
    const deCuantas = Number(row.rollup_de);
    if (deCuantas > 0) {
      avisos.push(
        `${row.row_type === 'phase' ? 'La fase' : 'El ticket'} no tiene fechas propias cargadas: ` +
        `esto es lo que suman sus ${deCuantas} tarea(s).`,
      );
    } else {
      avisos.push(
        `${row.row_type === 'phase' ? 'Esta fase' : 'Este caso'} no tiene tareas con fechas, ` +
        'asi que esta barra no sale de ningun plan: el sistema la dibujo desde el alta para que la ' +
        'fila no desaparezca. No mide nada.',
      );
    }
  }

  // El compromiso del ticket: lo prometido, contra lo que dice el plan. Es la pregunta que
  // el semaforo de SLA no contesta —un caso puede estar en verde y ya estar perdido—.
  const c = row.compromiso;
  if (c) {
    if (c.estado === 'no_entra') {
      campos.push({ k: 'Compromiso', v: dia(c.compromiso), nota: 'lo prometido', flojo: false });
      avisos.push(
        `El plan termina ${Math.abs(c.margen_dias)} dia(s) habil(es) despues de lo prometido. ` +
        'Con este plan, este caso no llega.',
      );
    } else if (c.estado === 'entra') {
      campos.push({
        k: 'Compromiso',
        v: dia(c.compromiso),
        nota: c.margen_dias === 0 ? 'llega justo' : `${c.margen_dias} d de margen`,
        flojo: false,
      });
    } else if (c.estado === 'sin_compromiso' && c.compromiso_roto) {
      avisos.push(
        'Este caso no tiene fecha comprometida porque no se pudo calcular. Eso no es ' +
        '"sin SLA": es configuracion a revisar.',
      );
    } else if (c.estado === 'sin_compromiso') {
      campos.push({ k: 'Compromiso', v: 'sin cierre', nota: 'el intent promete atencion', flojo: true });
    } else if (c.estado === 'sin_plan') {
      avisos.push('Ninguna tarea de este caso tiene fecha, asi que no hay plan que comparar.');
    }
  }

  // **Avance y consumo, separados y dichos.** Esto mostraba un solo numero que era el consumo
  // disfrazado de avance: `pct` salia de `consumed/estimate`. Ahora `progress` es lo hecho y
  // `consumo` lo gastado, y `null` en el avance significa que nadie lo declaro — que no es cero.
  const pct = row.progress == null ? null : Math.round(Number(row.progress) * 100);
  const consumoPct = row.consumo == null ? null : Math.round(Number(row.consumo) * 100);

  let nota;
  if (!esTarea) {
    nota = row.row_type === 'ticket'
      ? (row.avance_confiable === false
        ? 'sale del estado del caso, no de sus tareas'
        : 'pondera las horas estimadas de sus tareas')
      : 'pondera las horas estimadas de sus tareas';
  } else if (pct == null) {
    nota = 'nadie declaro cuanto esta hecho';
  } else if (!row.avance_declarado) {
    nota = 'la tarea esta terminada';
  } else {
    nota = 'lo declaro quien trabaja la tarea';
  }

  const progreso = {
    pct,
    nota,
    consumoPct,
    consumoNota: est
      ? `${row.consumed_hours || 0} h de ${est} h estimadas`
      : `${row.consumed_hours || 0} h, sin estimacion`,
    seFueDeMano: Boolean(row.consumio_mas_de_lo_que_avanzo),
  };

  // El aviso va primero entre los avisos porque es el unico que todavia se puede corregir: una
  // fecha vencida ya paso, esto esta pasando.
  if (row.consumio_mas_de_lo_que_avanzo) {
    avisos.unshift(
      `Se gasto mas de lo que se avanzo: ${row.consumed_hours || 0} h de ${est} h `
      + `estimadas, con ${pct} % declarado. Si la tarea estaba mal estimada, se puede re-estimar `
      + `diciendo por que.`,
    );
  }
  if (esTarea && pct == null && (Number(row.consumed_hours) || 0) > 0) {
    avisos.push(
      'Tiene horas imputadas y nadie declaro cuanto esta hecho, asi que no se puede saber si va '
      + 'bien o se esta yendo de mano.',
    );
  }

  return { tipoLabel: tipo.label, tipoNota: tipo.nota, campos, avisos, progreso };
}
